<template>
  <v-card class="pt-5 pb-6 px-6" rounded="lg" style="height: 100%;">
    <div class="d-flex align-center mb-2">
      <div class="d-flex flex-column">
        <span class="text-body-1">{{ title }}</span>
        <span v-if="subtitle" class="text-caption text-medium-emphasis">{{ subtitle }}</span>
      </div>
      <v-spacer></v-spacer>
      <slot name="actions"></slot>
    </div>
    <div v-if="isEmpty" class="d-flex align-center justify-center" :style="{ height: height + 'px' }">
      <span class="text-body-2 text-medium-emphasis">{{ $t('noData') }}</span>
    </div>
    <v-chart
      v-else
      ref="chartRef"
      class="chart"
      :style="{ height: height + 'px' }"
      :option="option"
      autoresize
      @click="handleChartClick"
    />
  </v-card>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useStore } from 'vuex';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  MarkLineComponent,
  MarkAreaComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';

// use() registers globally, so every v-chart in the app gets these.
use([
  CanvasRenderer,
  BarChart,
  LineChart,
  PieChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  MarkLineComponent,
  MarkAreaComponent,
]);

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  subtitle: {
    type: String,
    default: ''
  },
  buildOption: {
    type: Function,
    required: true
  },
  height: {
    type: Number,
    default: 400
  },
  // Bump to force a rebuild from the parent (bucket toggles, prop changes).
  refreshKey: {
    type: [String, Number],
    default: 0
  }
});

const emit = defineEmits(['chart-click']);

const store = useStore();
const chartRef = ref(null);
const option = ref({});
// buildOption returning null means "no data" - show the empty state rather
// than keeping the previous chart on screen.
const isEmpty = ref(false);

const rebuild = async () => {
  if (!store.getters.getDataLoaded) return;
  try {
    const newOption = await props.buildOption();
    if (newOption) {
      option.value = newOption;
      isEmpty.value = false;
    } else {
      isEmpty.value = true;
    }
  } catch (error) {
    console.error(`Error building chart option for "${props.title}":`, error);
    isEmpty.value = true;
  }
};

defineExpose({ chartRef, rebuild });

const handleChartClick = (params) => {
  emit('chart-click', params);
};

watch(
  () => store.getters.getDataLoaded,
  (loaded) => {
    if (loaded) rebuild();
  },
  { immediate: true }
);

watch(
  () => store.state.filterContext?.filtersLastUpdated?.value,
  () => {
    rebuild();
  }
);

watch(
  () => store.getters.chartFontColor,
  () => {
    rebuild();
  }
);

watch(
  () => props.refreshKey,
  () => {
    rebuild();
  }
);
</script>

<style scoped>
.chart {
  width: 100%;
  /* ECharts writes an inline pixel width onto this element; clipping keeps a
     stale measurement from widening the card. */
  max-width: 100%;
  overflow: hidden;
}
</style>
